// Operator endpoints (13 §Endpoints Operator, §Entitlements, §Gate tracker). Each company row
// comes from a per-company query under that company's RLS context (a fan-out, never a join).
import { z } from "zod";
import {
  AddonKey,
  AgentIssueKind,
  AgentIssueSource,
  AgentIssueStatus,
  Band,
  Currency,
  EntitlementSource,
  EntitlementStatus,
  FridayReportState,
  GateState,
  GateTargetSource,
  MembershipStatus,
  OperatorInboxKind,
  SystemAgentFeedbackKind,
  SystemAgentFeedbackSeverity,
  SystemAgentFixStatus,
  SystemAgentKeyScope,
  SystemAgentMessageAuthor,
  SystemAgentMessageKind,
} from "../enums";
import { Email, FreeText, IsoDate, NonEmpty, Slug, Timestamp, Uuid, WeekKey } from "../primitives";
import {
  Company,
  CompanyEntitlement,
  ConsumerProcessing,
  EntitlementCacheRow,
  Invitation,
  OrgCalendarConfig,
} from "../rows";
import { MetricGate } from "../scorecard";
import { OnboardingOpened } from "./client-onboarding";
import { DigitalEmployeeSummary, ScorecardSummary } from "./org";
import { defineRoute } from "./route";

const base = "/v1/operator";
const core = { feature: "core", tag: "operator", access: "operator" } as const;

export const OperatorWeekQuery = z.object({
  week: WeekKey.optional().describe("Default: current"),
});

export const OperatorCompanyRow = z
  .object({
    company: Company,
    de_count: z.int().min(0),
    band: z.enum(Band.options).nullable(),
    kpi_score: z.number().nullable(),
    cost_minor: z.int(),
    supervisor_minutes: z.int(),
    founder_minutes: z.int(),
    report_state: FridayReportState.nullable(),
    last_ingest_at: Timestamp.nullable(),
  })
  .meta({ id: "OperatorCompanyRow" });

export const OperatorCompanyCreate = z
  .strictObject({
    name: NonEmpty,
    slug: Slug,
    reporting_currency: Currency,
    calendar_config: OrgCalendarConfig,
    /**
     * The client's owner, invited by email. Omitted: an operator-managed company with no owner
     * invitation (internal pilots); the agency acts through `agency_operator` as it always does,
     * and an owner can be invited later from People.
     */
    owner: z.strictObject({ email: Email, display_name: NonEmpty }).optional(),
    /**
     * PLATFORM-OPERATOR: create the platform operator's own company (any slug): it takes the
     * install's owner-marked id (`cp.platform_setting platform_operator_company_id`; refused
     * while none is marked) and stays self-serve. Omitted or false: a client company (managed).
     */
    platform_operator: z.boolean().optional(),
    /**
     * ONBOARDING-P1 (CLIENT_ONBOARDING=1; refused while it is off): the client's plan
     * (`cp.plan.key`, active), set before onboarding. With `owner`, creating the client opens its
     * onboarding case and sends the owner the onboarding link at once. New clients start with no
     * digital employees.
     */
    plan_key: Slug.optional(),
    /** ONBOARDING-P1: the intake deadline in business days (default 5). */
    onboarding_due_days: z.int().min(1).max(30).optional(),
  })
  .meta({ id: "OperatorCompanyCreate" });

export const OperatorCompanyCreated = z
  .object({
    company: Company,
    owner_invitation: Invitation.nullable(),
    /** ONBOARDING-P1: the onboarding case opened with a plan (absent without one). */
    onboarding: OnboardingOpened.optional(),
  })
  .meta({ id: "OperatorCompanyCreated" });

/** Closes the live row and inserts the new one; writes `entitlement_cache` (13 §Entitlements). */
export const EntitlementPut = z
  .strictObject({
    feature_key: AddonKey,
    status: EntitlementStatus,
    // PROVISION-IN-PRODUCT: `rule` rows are the feature rules' own; an admin's row is an override.
    source: EntitlementSource.exclude(["bundled", "rule"]),
    starts_on: IsoDate,
    ends_on: IsoDate.nullable().optional(),
    note: z.string().max(2000).nullable().optional(),
  })
  .meta({ id: "EntitlementPut" });

export const CompanyEntitlements = z
  .object({
    company_id: Uuid,
    live: z.array(CompanyEntitlement),
    cache: z.array(EntitlementCacheRow),
  })
  .meta({ id: "CompanyEntitlements" });

export const OperatorDigitalEmployeeRow = z
  .object({
    company: z.object({ id: Uuid, slug: Slug, name: z.string() }),
    digital_employee: DigitalEmployeeSummary,
    scorecard: ScorecardSummary.nullable(),
    spend_alert: z.boolean().describe("LiteLLM spend ≥ 80 % of max_budget"),
    spend_attention: z
      .string()
      .nullable()
      .describe(
        "Why LiteLLM spend needs an operator: a reset/discontinuity, items held unbooked, or remote keys awaiting a compensating delete",
      ),
    // M5-05: the agency console's view of the DE in the week.
    week_key: WeekKey,
    supervisor: z
      .object({ seat_id: Uuid, title: z.string(), name: z.string().nullable() })
      .nullable()
      .describe("The supervisor seat of the DE's seat and its active holder"),
    /** Ledger rows of the week on the DE's seat, every visibility, in the reporting currency. */
    cost_minor: z.int(),
    currency: z.string().length(3),
    /** Approved, live supervision time on the DE's seat in the week. */
    supervision_minutes: z.int().min(0),
    /** Approvals the DE raised that wait for a decision now. */
    pending_approvals: z.int().min(0),
  })
  .meta({ id: "OperatorDigitalEmployeeRow" });

// SYSTEM-AGENT (founder, 2026-10-29): the platform's own agents in a company (the maintenance agent),
// shown to operators only — with their status and their feedback reports (operator-only table).
/**
 * SKILLSMITH-HUB (founder, 2026-10-30): the severity criteria, one line each — written into the
 * system agent's instructions and shown behind the hub's (i). Most urgent first.
 */
export const SKILLSMITH_SEVERITY_CRITERIA = {
  critical: "Client-facing harm, a data or security risk, money, or work stopped.",
  high: "Repeated failures, or a KPI at risk.",
  medium: "A quality issue or a skill gap.",
  low: "Polish or an idea.",
} as const satisfies Record<z.infer<typeof SystemAgentFeedbackSeverity>, string>;

/** Evidence a report rests on: a run, a verdict, a task, an issue-log entry, a correction… */
const Evidence = z.object({
  kind: z.string().min(1).max(64),
  id: z.string().min(1).max(200),
  note: z.string().max(500).optional(),
});

/**
 * SKILLSMITH-HUB: a structured change the report proposes for one of the company's agents.
 * Nothing is written until a super admin presses Apply in the hub; Revert writes the base back.
 */
export const SystemAgentProposal = z
  .object({
    kind: z.literal("how_i_work"),
    de_membership_id: Uuid,
    /** The "How I work" version the change was written against (0: none yet). */
    base_version: z.int().min(0),
    /** The full new text. */
    text: z.string().min(1).max(20000),
  })
  .meta({ id: "SystemAgentProposal" });

export const SystemAgentFeedbackReport = z
  .object({
    id: Uuid,
    company_id: Uuid,
    membership_id: Uuid,
    topic_id: Uuid,
    /** The report's category. */
    kind: SystemAgentFeedbackKind,
    severity: SystemAgentFeedbackSeverity,
    title: z.string(),
    body: z.string(),
    evidence: z.array(z.object({ kind: z.string(), id: z.string(), note: z.string().optional() })),
    affected_agents: z.array(z.string()),
    suggested_fix: z.string().nullable(),
    proposal: SystemAgentProposal.nullable(),
    fix_status: SystemAgentFixStatus,
    fix_decided_at: Timestamp.nullable(),
    reviewed_at: Timestamp.nullable(),
    created_at: Timestamp,
  })
  .meta({ id: "SystemAgentFeedbackReport" });

export const OperatorSystemAgentRow = z
  .object({
    company: z.object({ id: Uuid, slug: Slug, name: z.string() }),
    membership_id: Uuid,
    name: z.string(),
    status: MembershipStatus,
    /** When the membership last changed (for a converted DE: when it became a system agent). */
    since: Timestamp,
    /** The latest reports, newest first (at most 20). */
    reports: z.array(SystemAgentFeedbackReport),
    /** The caller is a platform super admin: it may open the agent's private space. */
    can_talk: z.boolean(),
  })
  .meta({ id: "OperatorSystemAgentRow" });

const SystemAgentParams = z.object({ company_id: Uuid, membership_id: Uuid });

export const SystemAgentSignalsQuery = z.object({
  days: z.coerce.number().int().min(1).max(90).optional().describe("Default: 7"),
});

export const SystemAgentSignals = z
  .object({
    since: Timestamp,
    runs: z.array(z.object({ state: z.string(), count: z.int() })),
    verdicts: z.array(z.object({ verdict: z.string(), count: z.int() })),
    failures: z.array(
      z.object({
        run_id: Uuid,
        membership_id: Uuid,
        name: z.string(),
        reason: z.string().nullable(),
        ended_at: z.string().nullable(),
      }),
    ),
    corrections: z.array(
      z.object({
        id: Uuid,
        de_membership_id: Uuid,
        text: z.string(),
        applied: z.string(),
        created_at: z.string(),
      }),
    ),
    skills: z.array(z.object({ key: z.string(), runs: z.int() })),
    /**
     * SKILLSMITH-HUB: what people and agents are doing, as counts only (never content): messages
     * by author kind, tasks created and completed, runs per agent.
     */
    usage: z.object({
      messages: z.array(z.object({ author_kind: z.string(), count: z.int() })),
      tasks_created: z.int(),
      tasks_completed: z.int(),
      runs_by_agent: z.array(z.object({ membership_id: Uuid, name: z.string(), runs: z.int() })),
    }),
    /**
     * SKILLSMITH-HUB: false for the system agent's own key — it gets counts only (no failure
     * reasons, no correction texts: it reads the issue log for those).
     */
    detailed: z.boolean(),
  })
  .meta({ id: "SystemAgentSignals" });

/**
 * SKILLSMITH-HUB: a report must name its company (equal to the path's: a report filed against the
 * wrong company is refused, never re-homed) and its severity; both are required at the API.
 */
export const SystemAgentFeedbackCreate = z
  .object({
    company_id: Uuid,
    severity: SystemAgentFeedbackSeverity,
    kind: SystemAgentFeedbackKind,
    title: z.string().min(1).max(200),
    body: z.string().min(1).max(20000),
    evidence: z.array(Evidence).max(50).optional(),
    affected_agents: z.array(z.string().min(1).max(120)).max(50).optional(),
    suggested_fix: z.string().min(1).max(5000).optional(),
    proposal: SystemAgentProposal.optional(),
    /** Issue-log entries this report answers (each moves to `reported`, linked to it). */
    issue_ids: z.array(Uuid).max(50).optional(),
  })
  .meta({ id: "SystemAgentFeedbackCreate" });

// SKILLSMITH-HUB (Andrei, 2026-10-30): the company's "Agent issues" log, as its system agent reads
// it — from its cursor, never the company's chats or work in bulk.
export const AgentIssue = z
  .object({
    id: Uuid,
    source: AgentIssueSource,
    reporter_membership_id: Uuid.nullable(),
    reporter_label: z.string().nullable(),
    kind: AgentIssueKind,
    severity: SystemAgentFeedbackSeverity,
    what: z.string(),
    context: z.string().nullable(),
    run_id: Uuid.nullable(),
    task_id: Uuid.nullable(),
    status: AgentIssueStatus,
    duplicate_of: Uuid.nullable(),
    report_id: Uuid.nullable(),
    created_at: Timestamp,
  })
  .meta({ id: "AgentIssue" });

export const AgentIssuesQuery = z.object({
  after: Uuid.optional().describe("Default: the stored cursor (what the agent has read up to)"),
  limit: z.coerce.number().int().min(1).max(100).optional().describe("Default: 50"),
});

export const AgentIssuePage = z
  .object({
    data: z.array(AgentIssue),
    /** The stored cursor (null: nothing read yet). */
    cursor: Uuid.nullable(),
    /** Pass to `issues/cursor` once these are handled (null: the page was empty). */
    next_cursor: Uuid.nullable(),
  })
  .meta({ id: "AgentIssuePage" });

/** Moves the stored cursor forward (a cursor at or before the stored one changes nothing). */
export const AgentIssueCursorPut = z.object({ cursor: Uuid }).meta({ id: "AgentIssueCursorPut" });

export const AgentIssueUpdate = z
  .object({
    status: AgentIssueStatus.exclude(["new"]),
    /** Required for `duplicate`: the entry it repeats (duplicates group under it). */
    duplicate_of: Uuid.optional(),
    /** Required for `reported`: the hub report it went into. */
    report_id: Uuid.optional(),
  })
  .meta({ id: "AgentIssueUpdate" });

/**
 * What the system agent may read for ONE entry it is investigating: the run, task and agent the
 * entry names — nothing else, never message text.
 */
export const AgentIssueContext = z
  .object({
    issue: AgentIssue,
    run: z
      .object({
        id: Uuid,
        state: z.string(),
        state_reason: z.string().nullable(),
        verdict: z.string().nullable(),
        membership_id: Uuid,
        created_at: Timestamp,
        ended_at: Timestamp.nullable(),
        steps: z.array(z.object({ kind: z.string(), at: Timestamp, tool: z.string().nullable() })),
      })
      .nullable(),
    task: z.object({ id: Uuid, title: z.string(), status: z.string().nullable() }).nullable(),
    agent: z.object({ membership_id: Uuid, name: z.string(), kind: z.string() }).nullable(),
    /** The linked DE's current "How I work" (version 0, text null: none yet); null for others. */
    how_i_work: z
      .object({ de_membership_id: Uuid, version: z.int().min(0), text: z.string().nullable() })
      .nullable(),
  })
  .meta({ id: "AgentIssueContext" });

/** SKILLSMITH-HUB cost: the system agent's spend today against its daily cap (UTC day). */
export const SystemAgentBudget = z
  .object({
    day: IsoDate,
    cap_micros: z.int().min(0),
    spent_micros: z.int().min(0),
    paused: z.boolean(),
  })
  .meta({ id: "SystemAgentBudget" });

/** One model turn's cost, booked to the company as a System cost line (idempotent per turn). */
export const SystemAgentSpendCreate = z
  .object({
    turn_id: z.string().min(1).max(200),
    amount_micros: z.int().min(0).max(1_000_000_000),
    model: z.string().min(1).max(120).optional(),
  })
  .meta({ id: "SystemAgentSpendCreate" });

// SYSTEM-AGENT (Andrei, 2026-10-29): a platform super admin's private space with a company's
// system agent — stored platform-side, never in the company's chat; every super-admin message is
// in the platform audit.
export const SystemAgentMessage = z
  .object({
    id: Uuid,
    author_kind: SystemAgentMessageAuthor,
    author_user_id: Uuid.nullable(),
    kind: SystemAgentMessageKind,
    investigation_id: Uuid.nullable(),
    body: z.string(),
    created_at: Timestamp,
  })
  .meta({ id: "SystemAgentMessage" });

export const SystemAgentSpace = z
  .object({
    company: z.object({ id: Uuid, slug: Slug, name: z.string() }),
    membership_id: Uuid,
    name: z.string(),
    /** Oldest first (the latest 200). */
    messages: z.array(SystemAgentMessage),
    investigations: z.array(
      z.object({
        id: Uuid,
        body: z.string(),
        created_at: Timestamp,
        state: z.enum(["open", "reported"]),
      }),
    ),
    /** Super-admin messages after the agent's last one (what it has yet to answer). */
    pending: z.int().min(0),
  })
  .meta({ id: "SystemAgentSpace" });

export const SystemAgentMessageCreate = z
  .object({
    body: z.string().min(1).max(20000),
    /**
     * Default `super_admin` (a person). `system_agent`: the platform's runtime of the agent posts
     * its answer, an investigation it opens, or its report on one (`investigation_id`).
     */
    author: SystemAgentMessageAuthor.optional(),
    kind: SystemAgentMessageKind.optional(),
    investigation_id: Uuid.optional(),
  })
  .meta({ id: "SystemAgentMessageCreate" });

/**
 * The agent's own runtime key. Sent as `Authorization: Bearer cosk_sa_…`, it is admitted on the
 * agent's own `signals` (`signals:read`), `feedback` (`feedback:write`) and `space` routes
 * (`space:write`: read the space and answer in it, as the agent) — nowhere else.
 */
export const SystemAgentKeyIssue = z
  .object({ scopes: z.array(SystemAgentKeyScope).min(1).max(3).optional() })
  .meta({ id: "SystemAgentKeyIssue" });

export const SystemAgentKeyIssued = z
  .object({
    id: Uuid,
    membership_id: Uuid,
    key_prefix: z.string(),
    /** The plaintext, returned once. */
    key: z.string(),
    scopes: z.array(SystemAgentKeyScope),
    /** Earlier active keys of the agent this one revoked. */
    revoked: z.int().min(0),
    created_at: Timestamp,
  })
  .meta({ id: "SystemAgentKeyIssued" });

// SKILLSMITH-HUB (founder, 2026-10-30): the operator console's "Agent health" — super admins only
// (404 to anyone else). One topic per company; the morning view across every granted company.
const HubCompany = z.object({ id: Uuid, slug: Slug, name: z.string() });

export const OperatorInboxItem = z
  .object({
    id: Uuid,
    kind: OperatorInboxKind,
    company: HubCompany,
    report_id: Uuid.nullable(),
    severity: SystemAgentFeedbackSeverity,
    title: z.string(),
    created_at: Timestamp,
  })
  .meta({ id: "OperatorInboxItem" });

export const AgentHealthQuery = z.object({
  since: Timestamp.optional().describe('Default: 24 hours ago ("since yesterday")'),
  severity: z
    .string()
    .max(64)
    .regex(/^(critical|high|medium|low)(,(critical|high|medium|low))*$/)
    .optional()
    .describe("Comma-separated severities to keep"),
  company_id: Uuid.optional(),
  status: z.enum(["unreviewed", "reviewed", "all"]).optional().describe("Default: all"),
});

const SeverityCounts = z.object({
  critical: z.int().min(0),
  high: z.int().min(0),
  medium: z.int().min(0),
  low: z.int().min(0),
});

export const AgentHealth = z
  .object({
    since: Timestamp,
    /** The severity criteria (the (i) popover). */
    criteria: z.object({
      critical: z.string(),
      high: z.string(),
      medium: z.string(),
      low: z.string(),
    }),
    /** New reports since `since`, filtered: severity (critical first), then company name. */
    reports: z.array(SystemAgentFeedbackReport.extend({ company: HubCompany })),
    /** Per company, unfiltered by severity: the new reports' counts and the open issue entries. */
    companies: z.array(
      z.object({
        company: HubCompany,
        topic_id: Uuid.nullable(),
        counts: SeverityCounts,
        total: z.int().min(0),
        unreviewed: z.int().min(0),
        open_issues: z.int().min(0),
        agents: z.array(z.object({ membership_id: Uuid, name: z.string(), status: z.string() })),
        budget: SystemAgentBudget.nullable(),
      }),
    ),
    totals: SeverityCounts,
    /** Open inbox items (Critical reports, paused agents), newest first. */
    inbox: z.array(OperatorInboxItem),
  })
  .meta({ id: "AgentHealth" });

const HubParams = z.object({ company_id: Uuid });
const HubReportParams = z.object({ company_id: Uuid, report_id: Uuid });

/** One company's topic: its name always in the header, its reports and messages, oldest first. */
export const AgentHealthTopic = z
  .object({
    company: HubCompany,
    topic_id: Uuid.nullable(),
    agents: z.array(z.object({ membership_id: Uuid, name: z.string(), status: z.string() })),
    items: z.array(
      z.discriminatedUnion("type", [
        z.object({
          type: z.literal("message"),
          at: Timestamp,
          membership_id: Uuid,
          message: SystemAgentMessage,
        }),
        z.object({ type: z.literal("report"), at: Timestamp, report: SystemAgentFeedbackReport }),
      ]),
    ),
    /** Super-admin messages after the agent's last one. */
    pending: z.int().min(0),
    open_issues: z.int().min(0),
    budget: SystemAgentBudget.nullable(),
  })
  .meta({ id: "AgentHealthTopic" });

export const AgentHealthMessageCreate = z
  .object({
    body: z.string().min(1).max(20000),
    /** Which system agent of the company (default: its only, or first active, one). */
    membership_id: Uuid.optional(),
  })
  .meta({ id: "AgentHealthMessageCreate" });

export const AgentHealthReview = z
  .object({ reviewed: z.boolean() })
  .meta({ id: "AgentHealthReview" });

/** Apply (a super admin's yes: the proposal is written, audited), reject, or revert an applied fix. */
export const AgentHealthFix = z
  .object({ action: z.enum(["apply", "reject", "revert"]) })
  .meta({ id: "AgentHealthFix" });

export const GateQuery = z.object({ as_of: IsoDate.optional() });

export const Gate = z
  .object({
    as_of: IsoDate,
    /**
     * PLATFORM-OPERATOR: the platform operator's own company the gates are defined on (its
     * metrics with a gate spec); null when none is marked or it was not readable.
     */
    company: z.object({ id: Uuid, slug: z.string(), name: z.string() }).nullable(),
    /** False when a population was not read in full; `incomplete_reasons` say which. */
    complete: z.boolean(),
    incomplete_reasons: z.array(z.string()),
    gates: z.array(
      z.object({
        key: z.string(),
        label: z.string(),
        /** From the gate's metric and spec (KPI data, never code). */
        unit: z.enum(["number", "percent", "currency", "time"]),
        direction: z.enum(["gte", "lte", "between"]),
        measure: MetricGate.shape.measure,
        condition: z.string(),
        fanout: z.enum(["supervision", "founder"]).nullable(),
        actual: z.number().nullable(),
        target: z.number().nullable(),
        due_on: IsoDate.nullable(),
        state: GateState,
        days_left: z.int().nullable(),
        note: z.string().nullable(),
        /** The upper bound of a range gate (close rate), else null. */
        target_max: z.number().nullable(),
        target_source: GateTargetSource,
      }),
    ),
    /** `triggered` is null when the observations it needs were not read in full. */
    kill_criteria: z.array(z.object({ text: z.string(), triggered: z.boolean().nullable() })),
    per_de: z.array(
      z.object({
        company: z.object({ id: Uuid, name: z.string() }),
        de: z.object({ membership_id: Uuid, display_name: z.string() }),
        supervisor_minutes_week: z.int(),
        founder_minutes_week: z.int(),
        day: z.int().min(0),
      }),
    ),
  })
  .meta({ id: "Gate" });

export const ConsumerRecordRef = z
  .strictObject({ company_id: Uuid, event_id: Uuid })
  .meta({ id: "ConsumerRecordRef" });
export const ConsumerSkip = ConsumerRecordRef.extend({
  reason: z.string().trim().min(1).max(2000),
}).meta({
  id: "ConsumerSkip",
});

const NameParams = z.object({ name: z.string().min(1).max(128) });
const OperatorCompanyParams = z.object({ company_id: Uuid });

const TenantName = z
  .string()
  .max(63)
  .regex(/^[a-z0-9][a-z0-9-]{0,62}$/);

/**
 * NEW-CLIENT-PROCEDURE: let a company have its Recall tenant made on a server whose Recall is
 * shared (`RECALL_TENANT_ONLY` set), in place of an env edit and a restart. Stored as the
 * company's `recall_tenant` provisioning setting (versioned, `cp.platform_audit`).
 */
export const RecallTenantPut = z
  .strictObject({
    enabled: z.boolean(),
    /** Replaces the company slug in the tenant name (`<prefix><tenant_name>`); default the slug. */
    tenant_name: TenantName.nullable().optional(),
  })
  .meta({ id: "RecallTenantPut" });

export const RecallTenantView = z
  .object({
    company_id: Uuid,
    enabled: z.boolean(),
    tenant_name: TenantName.nullable(),
    /**
     * What decides on this server: `open` (no closed list: every company), `listed` (the env
     * lists the slug), `operator` (let in by this setting), `closed` (neither), `killed`
     * (`RECALL_TENANT_ONLY=none`: no new tenant for anyone), `off` (Recall not configured).
     */
    gate: z.enum(["open", "listed", "operator", "closed", "killed", "off"]),
    /** The tenant's slug `recall.provision` makes, or null when it makes none. */
    tenant_slug: z.string().nullable(),
  })
  .meta({ id: "RecallTenantView" });

/**
 * NEW-CLIENT-PROCEDURE: the client's support channel with HTVA operator seats, before its owner
 * signs up. Each operator is a user of the caller's agency (by email), seated as a `member`
 * person of the company (never owner or admin); the channel is private, in the General space.
 */
export const SupportChannelCreate = z
  .strictObject({
    name: NonEmpty,
    description: FreeText.nullable().optional(),
    operators: z
      .array(z.strictObject({ email: Email, display_name: NonEmpty.optional() }))
      .min(1)
      .max(5),
  })
  .meta({ id: "SupportChannelCreate" });

export const SupportChannelResult = z
  .object({
    company_id: Uuid,
    channel_id: Uuid,
    /** False: the channel was there already (found by name); missing operators were added. */
    created: z.boolean(),
    operators: z.array(
      z.object({
        email: Email,
        membership_id: Uuid,
        seat: z.enum(["created", "reactivated", "held"]),
      }),
    ),
    members_added: z.int().min(0),
  })
  .meta({ id: "SupportChannelResult" });

export const operatorRoutes = {
  "operator.companies.list": defineRoute({
    ...core,
    id: "operator.companies.list",
    method: "get",
    path: `${base}/companies`,
    summary: "Granted companies with their weekly summary",
    query: OperatorWeekQuery,
    response: { status: 200, schema: z.object({ data: z.array(OperatorCompanyRow) }) },
    errors: [],
    emits: [],
  }),
  "operator.companies.create": defineRoute({
    ...core,
    id: "operator.companies.create",
    method: "post",
    path: `${base}/companies`,
    summary: "Create a client company, its agency grant, tuples and catalog; invite its owner",
    body: OperatorCompanyCreate,
    idempotency: "optional",
    response: { status: 201, schema: OperatorCompanyCreated },
    errors: ["conflict"],
    // `cell.create_company_with_access` makes the creating operator the first owner; in the same
    // transaction the flow grants the agency, invites the client's owner (a pending membership:
    // no access until the invitee accepts with a verified email) and retires the creator's
    // membership (`access.revoked` + `membership.deactivated`).
    emits: [
      "company.created",
      "membership.created",
      "access.granted",
      "agency_grant.created",
      "invite.created",
      "metric_def.created",
      "access.revoked",
      "membership.deactivated",
      "template.created",
    ],
  }),
  "operator.entitlements.get": defineRoute({
    ...core,
    id: "operator.entitlements.get",
    method: "get",
    path: `${base}/companies/{company_id}/entitlements`,
    summary: "Live entitlement rows and the cell cache of a company",
    params: OperatorCompanyParams,
    response: { status: 200, schema: CompanyEntitlements },
    errors: [],
    emits: [],
  }),
  "operator.entitlements.put": defineRoute({
    ...core,
    id: "operator.entitlements.put",
    method: "put",
    path: `${base}/companies/{company_id}/entitlements`,
    summary: "Set one add-on's entitlement (ADR-0016; dependencies enforced)",
    params: OperatorCompanyParams,
    body: EntitlementPut,
    response: { status: 200, schema: CompanyEntitlements },
    errors: ["addon_requires", "addon_required_by"],
    emits: ["company.entitlement_changed"],
  }),
  "operator.recallTenant.get": defineRoute({
    ...core,
    id: "operator.recallTenant.get",
    method: "get",
    path: `${base}/companies/{company_id}/recall-tenant`,
    summary: "Whether recall.provision may make the company's Recall tenant here, and why",
    params: OperatorCompanyParams,
    response: { status: 200, schema: RecallTenantView },
    errors: [],
    emits: [],
  }),
  "operator.recallTenant.put": defineRoute({
    ...core,
    id: "operator.recallTenant.put",
    method: "put",
    path: `${base}/companies/{company_id}/recall-tenant`,
    summary: "Let the company have its Recall tenant on a shared Recall (audited; no env edit)",
    params: OperatorCompanyParams,
    body: RecallTenantPut,
    // A control-plane setting and its `platform_audit` row (as provisioning.settings.put); no
    // company state, so no company event.
    safe: true,
    response: { status: 200, schema: RecallTenantView },
    errors: [],
    emits: [],
  }),
  "operator.supportChannel.create": defineRoute({
    ...core,
    id: "operator.supportChannel.create",
    method: "post",
    path: `${base}/onboarding/companies/{company_id}/support-channel`,
    summary: "Seat HTVA operators and create the client's private support channel (audited)",
    params: OperatorCompanyParams,
    body: SupportChannelCreate,
    response: { status: 200, schema: SupportChannelResult },
    errors: ["conflict"],
    emits: [
      "membership.created",
      "membership.updated",
      "access.granted",
      "invite.accepted",
      "channel.created",
      "channel.member_changed",
    ],
  }),
  "operator.digitalEmployees.list": defineRoute({
    ...core,
    id: "operator.digitalEmployees.list",
    method: "get",
    path: `${base}/digital-employees`,
    summary: "Every DE across granted companies with its weekly scorecard summary",
    query: OperatorWeekQuery,
    response: { status: 200, schema: z.object({ data: z.array(OperatorDigitalEmployeeRow) }) },
    errors: [],
    emits: [],
  }),
  "operator.systemAgents.list": defineRoute({
    ...core,
    id: "operator.systemAgents.list",
    method: "get",
    path: `${base}/system-agents`,
    summary: "Every system agent across granted companies, with its status and latest reports",
    response: { status: 200, schema: z.object({ data: z.array(OperatorSystemAgentRow) }) },
    errors: [],
    emits: [],
  }),
  "operator.systemAgents.signals": defineRoute({
    ...core,
    id: "operator.systemAgents.signals",
    method: "get",
    // Not under `/companies/{company_id}/…` (the operator company scope): the agent is read under
    // the tenant role and the report written under the operator role, one after the other.
    path: `${base}/system-agents/{company_id}/{membership_id}/signals`,
    summary:
      "What a system agent reads: the company's runs, verdicts, failures, corrections and skill usage (read only)",
    params: SystemAgentParams,
    query: SystemAgentSignalsQuery,
    response: { status: 200, schema: SystemAgentSignals },
    errors: ["not_found"],
    emits: [],
  }),
  "operator.systemAgents.space.get": defineRoute({
    ...core,
    id: "operator.systemAgents.space.get",
    method: "get",
    path: `${base}/system-agents/{company_id}/{membership_id}/space`,
    summary:
      "A platform super admin's private space with the system agent: messages, investigations, what waits (super admins only; 404 to anyone else)",
    params: SystemAgentParams,
    response: { status: 200, schema: SystemAgentSpace },
    errors: ["not_found"],
    emits: [],
  }),
  "operator.systemAgents.space.post": defineRoute({
    ...core,
    id: "operator.systemAgents.space.post",
    method: "post",
    path: `${base}/system-agents/{company_id}/{membership_id}/space/messages`,
    summary:
      "Write in the private space: a super admin's message (audited), or the agent's answer, investigation or report",
    params: SystemAgentParams,
    body: SystemAgentMessageCreate,
    // Stored platform-side (operator-only) and audited in `cp.platform_audit`; an event in the
    // company's log would show the company its system agent. A read for the event rules.
    safe: true,
    response: { status: 201, schema: SystemAgentMessage },
    errors: ["not_found", "validation_failed"],
    emits: [],
  }),
  "operator.systemAgents.feedback.create": defineRoute({
    ...core,
    id: "operator.systemAgents.feedback.create",
    method: "post",
    path: `${base}/system-agents/{company_id}/{membership_id}/feedback`,
    summary: "File a system agent's feedback report (the operator-only store)",
    params: SystemAgentParams,
    body: SystemAgentFeedbackCreate,
    // No company state changes: the report goes to the operator-only store, and an event in the
    // company's log would show the company its system agent. A read for the event rules.
    safe: true,
    response: { status: 201, schema: SystemAgentFeedbackReport },
    errors: ["not_found", "validation_failed"],
    emits: [],
  }),
  // SKILLSMITH-HUB (Andrei, 2026-10-30): the company's Agent issues log, for its system agent's
  // key (`signals:read` reads, `feedback:write` moves status) or a super admin; 404 to anyone else.
  "operator.systemAgents.issues.list": defineRoute({
    ...core,
    id: "operator.systemAgents.issues.list",
    method: "get",
    path: `${base}/system-agents/{company_id}/{membership_id}/issues`,
    summary:
      "The company's Agent issues log after a cursor (default: the stored one), oldest first",
    params: SystemAgentParams,
    query: AgentIssuesQuery,
    response: { status: 200, schema: AgentIssuePage },
    errors: ["not_found"],
    emits: [],
  }),
  "operator.systemAgents.issues.cursor": defineRoute({
    ...core,
    id: "operator.systemAgents.issues.cursor",
    method: "post",
    path: `${base}/system-agents/{company_id}/{membership_id}/issues/cursor`,
    summary: "Move the stored issue-log cursor forward (never back)",
    params: SystemAgentParams,
    body: AgentIssueCursorPut,
    // Operator-only bookkeeping: no company state, no company event.
    safe: true,
    response: { status: 200, schema: AgentIssuePage.pick({ cursor: true }) },
    errors: ["not_found", "validation_failed"],
    emits: [],
  }),
  "operator.systemAgents.issues.update": defineRoute({
    ...core,
    id: "operator.systemAgents.issues.update",
    method: "post",
    path: `${base}/system-agents/{company_id}/{membership_id}/issues/{issue_id}`,
    summary:
      "Move an entry's status: investigating, reported (its report), resolved, duplicate (of one), wont_fix",
    params: SystemAgentParams.extend({ issue_id: Uuid }),
    body: AgentIssueUpdate,
    safe: true,
    response: { status: 200, schema: AgentIssue },
    errors: ["not_found", "validation_failed"],
    emits: [],
  }),
  "operator.systemAgents.issues.context": defineRoute({
    ...core,
    id: "operator.systemAgents.issues.context",
    method: "get",
    path: `${base}/system-agents/{company_id}/{membership_id}/issues/{issue_id}/context`,
    summary:
      "For ONE entry under investigation: the run, task and agent it names (read only; nothing else)",
    params: SystemAgentParams.extend({ issue_id: Uuid }),
    response: { status: 200, schema: AgentIssueContext },
    errors: ["not_found", "conflict"],
    emits: [],
  }),
  "operator.systemAgents.budget": defineRoute({
    ...core,
    id: "operator.systemAgents.budget",
    method: "get",
    path: `${base}/system-agents/{company_id}/{membership_id}/budget`,
    summary: "The system agent's spend today against its daily cap (paused at the cap)",
    params: SystemAgentParams,
    response: { status: 200, schema: SystemAgentBudget },
    errors: ["not_found"],
    emits: [],
  }),
  "operator.systemAgents.spend": defineRoute({
    ...core,
    id: "operator.systemAgents.spend",
    method: "post",
    path: `${base}/system-agents/{company_id}/{membership_id}/spend`,
    summary:
      "Book one model turn's cost to the company as a System cost line (idempotent per turn); answers the budget after it",
    params: SystemAgentParams,
    body: SystemAgentSpendCreate,
    response: { status: 200, schema: SystemAgentBudget },
    errors: ["not_found", "conflict"],
    emits: ["cost.posted"],
  }),
  // SKILLSMITH-HUB (founder, 2026-10-30): the operator console's Agent health (super admins only).
  "operator.agentHealth.get": defineRoute({
    ...core,
    id: "operator.agentHealth.get",
    method: "get",
    path: `${base}/agent-health`,
    summary:
      "Agent health's morning view: every granted company's new system-agent reports (severity, then company), counts, open issues and the super admins' inbox",
    query: AgentHealthQuery,
    response: { status: 200, schema: AgentHealth },
    errors: ["not_found"],
    emits: [],
  }),
  "operator.agentHealth.topic": defineRoute({
    ...core,
    id: "operator.agentHealth.topic",
    method: "get",
    path: `${base}/agent-health/{company_id}`,
    summary: "One company's topic: its system agents' reports and the super admins' messages",
    params: HubParams,
    response: { status: 200, schema: AgentHealthTopic },
    errors: ["not_found"],
    emits: [],
  }),
  "operator.agentHealth.message": defineRoute({
    ...core,
    id: "operator.agentHealth.message",
    method: "post",
    path: `${base}/agent-health/{company_id}/messages`,
    summary: "A super admin asks in the company's topic; its system agent answers there (woken)",
    params: HubParams,
    body: AgentHealthMessageCreate,
    safe: true,
    response: { status: 201, schema: SystemAgentMessage },
    errors: ["not_found", "validation_failed"],
    emits: [],
  }),
  "operator.agentHealth.review": defineRoute({
    ...core,
    id: "operator.agentHealth.review",
    method: "post",
    path: `${base}/agent-health/{company_id}/reports/{report_id}/review`,
    summary: "Mark a report reviewed (closes its inbox item) or unreviewed",
    params: HubReportParams,
    body: AgentHealthReview,
    safe: true,
    response: { status: 200, schema: SystemAgentFeedbackReport },
    errors: ["not_found"],
    emits: [],
  }),
  "operator.agentHealth.fix": defineRoute({
    ...core,
    id: "operator.agentHealth.fix",
    method: "post",
    path: `${base}/agent-health/{company_id}/reports/{report_id}/fix`,
    summary:
      "Apply a report's proposed change (a super admin's yes; written as a new version, audited), reject it, or revert an applied one",
    params: HubReportParams,
    body: AgentHealthFix,
    response: { status: 200, schema: SystemAgentFeedbackReport },
    errors: ["not_found", "conflict", "validation_failed"],
    // AGENT-SPEC: with an agent spec, the DE's next signed version (its How I work part).
    emits: ["digital_employee.updated", "agent_spec.versioned"],
  }),
  "operator.systemAgents.key.issue": defineRoute({
    ...core,
    id: "operator.systemAgents.key.issue",
    method: "post",
    path: `${base}/system-agents/{company_id}/{membership_id}/key`,
    summary:
      "Issue the system agent's own runtime key (`cosk_sa_`, shown once; revokes the earlier one): signals, reports and the space only (super admins only)",
    params: SystemAgentParams,
    body: SystemAgentKeyIssue,
    // Operator-only store and an audit row; no company state, so no company event.
    safe: true,
    response: { status: 201, schema: SystemAgentKeyIssued },
    errors: ["not_found"],
    emits: [],
  }),
  "operator.gate.get": defineRoute({
    ...core,
    id: "operator.gate.get",
    method: "get",
    path: `${base}/gate`,
    summary: "Gate tracker: the platform operator's gates (its metrics with a gate spec)",
    query: GateQuery,
    response: { status: 200, schema: Gate },
    /** `rate_limited`: the process already runs its maximum of gate requests (S1-17 #6). */
    errors: ["rate_limited"],
    emits: [],
  }),
  "operator.consumers.list": defineRoute({
    ...core,
    id: "operator.consumers.list",
    method: "get",
    path: `${base}/consumers`,
    summary: "Failed consumer processing records",
    response: { status: 200, schema: z.object({ data: z.array(ConsumerProcessing) }) },
    errors: [],
    emits: [],
  }),
  "operator.consumers.retry": defineRoute({
    ...core,
    id: "operator.consumers.retry",
    method: "post",
    path: `${base}/consumers/{name}/retry`,
    summary: "Retry a failed record (replayed in event_seq order)",
    params: NameParams,
    body: ConsumerRecordRef,
    idempotency: "optional",
    response: { status: 200, schema: ConsumerProcessing },
    errors: ["conflict"],
    emits: ["consumer.record_retried"],
  }),
  "operator.consumers.skip": defineRoute({
    ...core,
    id: "operator.consumers.skip",
    method: "post",
    path: `${base}/consumers/{name}/skip`,
    summary: "Mark a failed record skipped_by_operator",
    params: NameParams,
    body: ConsumerSkip,
    idempotency: "optional",
    response: { status: 200, schema: ConsumerProcessing },
    errors: ["conflict"],
    emits: ["consumer.record_skipped"],
  }),
} as const;
